import { formatDate } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { ChangeDetectionStrategy, Component, Input, OnChanges } from "@angular/core";
import { pushError } from "@app/_helpers/globalErrorHandler";
import { ClockService } from "@app/_services/clock.service";
import { AppService } from "@app/app.service";
import { TimerService } from "@app/pages/timer-home-page/timer-calendar-view.service";
import { RecordToolbarService } from "@app/shared/record-toolbar/record-toolbar.service";
import { ComegoQuery, ComegoTime, UserSettingsQuery } from "@timeghost/web-api";
import { addMinutes, isSameDay, roundToNearestMinutes, startOfMinute } from "date-fns/esm";
import { combineLatest, map, Observable, ReplaySubject } from "rxjs";

export interface CurrentTimeIndicatorConfig {
	dayStartHour: number;
	dayEndHour: number;
	segmentWidthPx: number;
	timelineWidthPx: number;
}

export function getCurrentTimeLeftPx(now: Date, config: CurrentTimeIndicatorConfig): number {
	const hourLabelOffsetPx = config.segmentWidthPx / 2;
	const minutesOfDay = (now.getHours() - config.dayStartHour) * 60 + now.getMinutes() + now.getSeconds() / 60;
	const dayMinutes = (config.dayEndHour - config.dayStartHour) * 60;
	const fraction = Math.min(1, Math.max(0, minutesOfDay / dayMinutes));
	return hourLabelOffsetPx + fraction * config.timelineWidthPx;
}

@Component({
	selector: "tg-current-time-indicator",
	templateUrl: "./current-time-indicator.component.html",
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CurrentTimeIndicatorComponent implements OnChanges {
	@Input() dayStartHour = 0;
	@Input() dayEndHour = 24;
	@Input() segmentWidthPx = 55;
	@Input() timelineWidthPx = 0;

	private readonly config$ = new ReplaySubject<CurrentTimeIndicatorConfig>(1);

	readonly leftPx$: Observable<number> = combineLatest([this.clock.now$, this.config$]).pipe(
		map(([now, config]) => getCurrentTimeLeftPx(now, config))
	);

	readonly hourLableHeightOffset = 56;

	@Input() todayIndex = -1;
	@Input() dayHeightPx = 0;

	get todayVisible(): boolean {
		return this.todayIndex >= 0;
	}

	constructor(
		private clock: ClockService,
		private recordToolbarService: RecordToolbarService,
		private comegoQuery: ComegoQuery,
		private userSettingsQuery: UserSettingsQuery,
		private appService: AppService,
		private calendarService: TimerService
	) {}

	ngOnChanges(): void {
		this.config$.next({
			dayStartHour: this.dayStartHour,
			dayEndHour: this.dayEndHour,
			segmentWidthPx: this.segmentWidthPx,
			timelineWidthPx: this.timelineWidthPx,
		});
	}
	readonly hasActiveWorkinghourRecording$ = this.calendarService.hasActiveWorkinghourRecording$;
	readonly hasActiveProjecthourRecording$ = this.calendarService.hasActiveProjecthourRecording$;

	async startWorkinghourRecording() {
		const now = roundToNearestMinutes(new Date(), { nearestTo: 1 });
		const todayEntries = this.comegoQuery.getAll({
			filterBy: (x) => x.user?.id === this.userSettingsQuery.getValue().id && isSameDay(new Date(x.start), now),
		});
		const overlapping = todayEntries.some((entry) => {
			if (entry.type !== "work" && entry.type !== "pause") return false;
			const start = new Date(entry.start);
			const end = entry.end ? new Date(entry.end) : null;
			if (!end) return now >= start;
			return now >= start && now < end;
		});
		if (overlapping) {
			this.appService.notifyError("errors.times.comego.already_recording");
			return false;
		}
		const cuser = this.userSettingsQuery.getValue();
		const user = cuser.workspace.users.find((x) => x.id === cuser.id);
		const start = roundToNearestMinutes(new Date(), { nearestTo: 1 });
		const checkinData = {
			name: null,
			start,
			end: null,
			type: "work",
			user,
		} as any;
		await this.recordToolbarService.startCheckin(checkinData).catch(async (err: HttpErrorResponse) => {
			pushError(err);
			return false;
		});
		return true;
	}
	async stopWorkinghourRecording() {
		const userId = this.userSettingsQuery.getValue()?.id;
		const times = this.comegoQuery.getAll({
			filterBy: (x: any) => x.user?.id === userId && !x.end && !(x as ComegoTime & { deleted?: boolean }).deleted,
		}) as ComegoTime[];
		if (!times.length) return;
		const time = times.sort((a, b) => new Date(b.start).getTime() - new Date(a.start).getTime())[0];
		const pairedWhId = time.id;
		const stopResult = await this.recordToolbarService.stopComegoWithRuleCheck(time, () =>
			this.recordToolbarService.performNormalComegoStop(time)
		);
		if (pairedWhId && stopResult.entry != null) {
			await this.recordToolbarService.afterPairedWorkingHoursStopped(pairedWhId, true);
		}
		return stopResult;
	}

	startProjecthourRecording() {
		this.recordToolbarService.startRecord();
	}
	stopProjecthourRecording() {
		this.recordToolbarService.stopRecord();
	}
}
